import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'neutral' | 'link';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      className = '',
      type = 'button',
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none';

    const variantStyles = {
      primary:
        'bg-primary-900 text-white hover:bg-primary-800 hover:shadow-lg hover:shadow-primary-900/10 disabled:bg-primary-300 disabled:shadow-none active:scale-[0.98]',
      secondary:
        'bg-white/90 backdrop-blur-sm text-gray-600 border border-gray-200 hover:bg-white hover:border-gray-300 hover:text-gray-900 active:scale-[0.98]',
      ghost:
        'bg-transparent text-gray-700 hover:bg-gray-50',
      danger:
        'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500',
      success:
        'bg-green-600 text-white hover:bg-green-700',
      neutral:
        'text-neutral-500 bg-transparent hover:text-neutral-900 hover:bg-neutral-100',
      link:
        'text-neutral-400 hover:text-primary-900 bg-transparent p-0 h-auto font-medium group',
    };

    const sizeStyles = {
      xs: 'px-3 py-1.5 text-xs rounded-md gap-1.5',
      sm: 'px-4 py-2.5 sm:py-1.5 text-[12px] rounded-lg gap-1.5',
      md: 'px-4 py-2 text-sm rounded-lg gap-2',
      lg: 'h-11 sm:h-[46px] px-6 text-sm rounded-xl gap-2 font-semibold',
    };

    const widthStyles = fullWidth ? 'w-full' : '';

    const linkSizeOverride = variant === 'link' ? 'gap-1.5' : sizeStyles[size];
    const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${linkSizeOverride} ${widthStyles} ${className}`.trim();

    return (
      <button
        ref={ref}
        type={type}
        className={combinedClassName}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          leftIcon
        )}
        {variant === 'link' ? <span>{children}</span> : children}
        {!loading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
